import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { CommonRecognitionArtifactFragmentFragment } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import Avatars from "@components/avatar/avatars";
import CoreValueIcon from "@components/core-value-icon/core-value-icon";
import AppLink from "@components/link/link";
import TimeAgoCustom from "@components/time-ago/time-ago";
import {
  assertEdgesNonNull,
  assertNonNull,
  getUrl,
  toWithBackground,
} from "@helpers/helpers";

const maxRecipients = 4;

const RecognitionBoardItem = ({
  recognition,
}: {
  recognition: CommonRecognitionArtifactFragmentFragment;
}) => {
  const location = useLocation<TFLocationState>();

  const renderRecipientNames = useCallback(
    (recognition: CommonRecognitionArtifactFragmentFragment) => {
      const maxWithExtraCopy = maxRecipients - 1;
      const recipients = recognition.recipients
        ? assertEdgesNonNull(recognition.recipients)
        : [];
      if (recipients.length <= maxWithExtraCopy) {
        return recipients.map(({ name }) => name).join(", ");
      }
      const names = recipients
        .map(({ name }) => name)
        .slice(0, maxWithExtraCopy)
        .join(", ");
      return `${names} & ${recipients.length - maxWithExtraCopy} others`;
    },
    [],
  );

  // Render
  const recipients = recognition.recipients
    ? assertEdgesNonNull(recognition.recipients)
    : [];
  return (
    <AppLink
      className="text-gray-800 p-6 bg-white rounded-lg border hover:border-blue-link flex flex-col gap-6"
      key={recognition.id}
      to={toWithBackground({
        pathname: getUrl({
          artifactId: recognition.id,
          artifactType: assertNonNull(recognition.artifactType),
        }),
        location,
      })}
    >
      <div className="flex w-full justify-center gap-2 md:gap-4">
        <div className="flex justify-center shrink-0 relative">
          <Avatars
            users={recipients}
            max={maxRecipients}
            size={14}
            extraClassName="text-sm"
            modalTitle="Recipients"
          />
          <span className="z-1 absolute -bottom-1 -right-1 h-[24px] w-[24px]">
            <CoreValueIcon
              emoji={recognition.recognitionCoreValue?.emoji}
              image={recognition.recognitionCoreValue?.image}
              imagePxSize={24}
            />
          </span>
        </div>
      </div>
      <div>
        <div className="text-center font-medium">
          {renderRecipientNames(recognition)}
        </div>
        {recognition.recognitionCoreValue && (
          <div className="text-center mt-0.5">
            {recognition.recognitionCoreValue.title}
          </div>
        )}
      </div>

      <div className="text-left text-gray-600 leading-6 wrap-break-word">
        {recognition.title}
      </div>

      <div className="flex justify-between gap-2 text-xs text-gray-500 tracking-tight">
        <div>
          From:{" "}
          <span className="font-medium">
            {assertNonNull(recognition.creator).name}
          </span>
        </div>
        <TimeAgoCustom date={recognition.created} />
      </div>
    </AppLink>
  );
};

export default RecognitionBoardItem;
